import React, { useEffect, useState } from 'react';
import propTypes from 'prop-types';
import { Select } from '../Select';
import { getOptions } from '../TireFinder/data/api';

const getValidParams = (currentStepIndex, params, inputs) => {
  const validParams = {};

  // only parameters before the current step are valid
  inputs.slice(0, currentStepIndex).forEach((input) => {
    validParams[input.attributeName] = params[input.attributeName];
  });

  return validParams;
};

const SelectGroup = ({ inputs, onReadyChange }) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [searchParams, setSearchParams] = useState({});
  const [isReady, setIsReady] = useState(false);
  const [steps, setSteps] = useState(() => (
    inputs.map((input, index) => ({
      ...input,
      onChange: async (value) => {
        if (value == null) {
          return;
        }

        const lastStepIndex = inputs.length - 1;
        const nextStepIndex = index + 1;
        let newSearchParams = searchParams;
        setSearchParams((params) => {
          newSearchParams = {
            ...getValidParams(index, params, inputs),
            [input.attributeName]: value,
          };

          return newSearchParams;
        });

        if (nextStepIndex <= lastStepIndex) {
          const options = await getOptions({
            method: steps[nextStepIndex].method,
            params: newSearchParams,
          });

          setSteps((inputSteps) => {
            const updatedInputSteps = [];
            inputSteps.forEach((step, stepIndex) => {
              updatedInputSteps.push({
                ...step,
                options: stepIndex === nextStepIndex ? options : step.options,
              });
            });

            return updatedInputSteps;
          });
        }

        setCurrentStepIndex(nextStepIndex);
      },
    }))
  ));

  useEffect(() => {
    setIsReady(steps.every((step) => !!searchParams[step.attributeName]));
  }, [steps, searchParams]);

  useEffect(() => {
    onReadyChange(isReady);
  }, [isReady, onReadyChange]);

  return (
    <div className={`cmp-tire-finder__select-group cmp-tire-finder__select-group--${steps.length}-steps`} data-testid="selectgroup">
      {steps.map(({
        method, options, displayName, inputName, onChange,
      }, index) => {
        const isInputActiveStep = index === currentStepIndex;
        const isInputAfterCurrentStep = index > currentStepIndex;
        const isInputAfterPreviousStep = index > currentStepIndex - 1;
        return (
          <Select
            key={method}
            options={options}
            id={method.replace('.', '_')}
            name={inputName}
            label={displayName}
            placeholder={`Select ${displayName}...`}
            isDisabled={isInputAfterCurrentStep}
            shouldFocusInput={isInputActiveStep && index > 0}
            shouldClearSelection={isInputAfterPreviousStep}
            onChange={onChange}
          />
        );
      })}
    </div>
  );
};

SelectGroup.propTypes = {
  inputs: propTypes.arrayOf(propTypes.shape({
    method: propTypes.string,
    displayName: propTypes.string,
    options: propTypes.arrayOf(propTypes.shape({
      value: propTypes.oneOfType([
        propTypes.string,
        propTypes.number,
      ]),
      label: propTypes.oneOfType([
        propTypes.string,
        propTypes.number,
      ]),
    })),
  })),
  onReadyChange: propTypes.func,
};

export { SelectGroup };
